import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Card } from 'components/shadcn/ui/card';
import { Button } from 'components/shadcn/ui/button';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import { addSpaceBeforeEveryCapitalLetter } from 'components/HGForm/helpers';
import HGError from 'components/HGForm/HGError';
import getValidationSchema from 'components/HGForm/formValidations';
import EditableTable from './EditableTable';
import HGSuccessCard from 'components/HGSuccessCard';
import { useGetSiteData } from 'components/SiteDetails/helper';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { updateDevice, useDevice } from 'components/DeviceDetails/service';
import { useJwtToken } from 'helper';

const EditDevices = () => {
    const accessToken = useJwtToken();
    const { Thing_ID } = useGetIDsFromURL();
    const { data: gatewayData } = useDevice(Thing_ID);
    const { Site_ID } = gatewayData || { Site_ID: null };
    const { data: site } = useGetSiteData(Site_ID);
    const [errorMessage, setErrorMessage] = useState(null);
    const [modalOpen, setModalOpen] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const [initialFieldsValues, setInitialFieldsValues] = useState({
        serialNumber: '',
        name: '',
        type: '',
        siteAttachedTo: '',
        existingDevices: [],
        newDevices: [],
    });

    useEffect(() => {
        if (gatewayData) {
            const { SerialNumber, ThingName, ThingType } = gatewayData;

            setInitialFieldsValues({
                serialNumber: SerialNumber ?? '',
                name: ThingName ?? '',
                type: ThingType ?? 'HomeGrid Devices',
                siteAttachedTo: site?.Name ?? 'No Site Attached',
                existingDevices: gatewayData.Microinverters ?? [],
                newDevices: [],
            });
        }
    }, [gatewayData, site]);

    const fieldKeysArray = Object.keys(initialFieldsValues);
    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            if (key === 'newDevices') {
                acc[key] = Yup.array().of(
                    Yup.object().shape({
                        serialNumber: Yup.string().required(
                            'Serial Number is required'
                        ),
                    })
                );
                return acc;
            }
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);

            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const updateMicroinverters = async (values) => {
        setErrorMessage(null);
        setSubmitting(true);
        try {
            const input = {
                Thing_ID,
                Customer_ID: gatewayData.Customer_ID,
                SerialNumber: values.serialNumber,
                ThingName: values.name,
                Microinverters: [
                    ...values.existingDevices,
                    ...values.newDevices,
                ],
                _version: gatewayData._version,
            };
            const updatedThing = await updateDevice(accessToken, input);
            if (updatedThing?.data?.updateThing) {
                setSuccess(true);
                setSubmitting(false);
            }
        } catch (error) {
            console.error('Error updating site: ', error);
            setErrorMessage('Error updating site');
            setSubmitting(false);
        }
    };

    const updateBatteries = async (values) => {
        setErrorMessage(null);
        setSubmitting(true);
        setTimeout(() => {
            console.log('updating batteries', values);
            setSubmitting(false);
        }, 1000);
    };

    if (success)
        return (
            <HGSuccessCard
                buttons={[
                    {
                        buttonText: 'back to gateway',
                        onClick: () => navigate(`/devices/${Thing_ID}`),
                    },
                ]}
            >
                Gateway updated successfully.
                <br />
                Click below to go back to gateway profile.
            </HGSuccessCard>
        );

    return (
        <Card className="p-3">
            <h4 className="text-2xl font-semibold">
                Edit {Thing_ID.includes('NEP') ? 'Microinverters' : 'Batteries'}
            </h4>
            <Formik
                initialValues={initialFieldsValues}
                validationSchema={validationSchema}
                onSubmit={
                    initialFieldsValues.type === 'NEP_GATEWAY'
                        ? updateMicroinverters
                        : updateBatteries
                }
                validateOnBlur={false}
                enableReinitialize
            >
                {({ values }) => (
                    <Form>
                        <EditableTable
                            openDeleteModal={(item) => setModalOpen(item)}
                        />
                        {errorMessage && (
                            <HGError
                                errorMessage={errorMessage}
                                sx={{ mb: 3 }}
                            />
                        )}
                        <div className="mt-5 flex justify-end">
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/devices/${Thing_ID}`)}
                                className="mr-2"
                            >
                                cancel
                            </Button>
                            <HGFormSubmitBtn
                                loading={submitting}
                                disabled={
                                    values.newDevices.length > 0 &&
                                    values.newDevices.some(
                                        (device) =>
                                            !device.serialNumber &&
                                            !device.validated
                                    )
                                        ? true
                                        : false
                                }
                            >
                                save
                            </HGFormSubmitBtn>
                        </div>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

export default EditDevices;
